import { useChat } from '@ai-sdk/react';
import { useAuth } from '../context/AuthContext';
import { useRef, useEffect, useCallback, useState } from 'react';
import axios from 'axios';

const BFF_URL = process.env.REACT_APP_AI_BFF_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const USE_BFF = !!BFF_URL;

export default function useAIChat(conversationId) {
    const { getAuthHeader } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [loadingConvs, setLoadingConvs] = useState(true);
    const messagesEndRef = useRef(null);
    // Legacy state (used when BFF not available)
    const [legacyMessages, setLegacyMessages] = useState([]);
    const [legacyLoading, setLegacyLoading] = useState(false);
    const [legacyInput, setLegacyInput] = useState('');
    const [activeConvId, setActiveConvId] = useState(conversationId || null);

    const headers = getAuthHeader();

    // AI SDK streaming chat (only active when BFF is configured)
    const chat = useChat({
        api: USE_BFF ? `${BFF_URL}/ai/chat` : '/ai/noop',
        body: { conversation_id: activeConvId || undefined },
        headers,
        onError: (err) => {
            console.error('AI Chat error:', err);
        },
    });

    // Auto-scroll on new messages
    const currentMessages = USE_BFF ? chat.messages : legacyMessages;
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [currentMessages]);

    // Normalize messages for backward compatibility
    const messages = currentMessages.map((m) => ({
        ...m,
        message_id: m.id || m.message_id,
    }));

    const loading = USE_BFF
        ? (chat.status === 'streaming' || chat.status === 'submitted')
        : legacyLoading;

    // Conversation list (always from Python backend)
    const loadConversations = useCallback(async () => {
        try {
            const res = await fetch(`${BACKEND_URL}/api/ai/conversations`, { headers });
            if (res.ok) setConversations(await res.json());
        } catch {} finally { setLoadingConvs(false); }
    }, [headers]);

    const loadConversation = useCallback(async (convId) => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/ai/conversations/${convId}`, { headers: getAuthHeader() });
            setLegacyMessages(res.data.messages || []);
            setActiveConvId(convId);
        } catch {}
    }, [getAuthHeader]);

    const startNewConversation = useCallback(() => {
        setActiveConvId(null);
        setLegacyMessages([]);
        if (USE_BFF) chat.setMessages([]);
    }, [chat]);

    // Send message
    const sendMessage = useCallback(async (text) => {
        if (!text?.trim() || loading) return null;

        if (USE_BFF) {
            chat.append({ role: 'user', content: text });
            return text;
        }

        // Legacy: direct call to Python backend
        const userMsg = { message_id: `temp-${Date.now()}`, id: `temp-${Date.now()}`, role: 'user', content: text, timestamp: new Date().toISOString() };
        setLegacyMessages(prev => [...prev, userMsg]);
        setLegacyLoading(true);
        try {
            const res = await axios.post(`${BACKEND_URL}/api/ai/chat`, {
                message: text,
                conversation_id: activeConvId || undefined,
            }, { headers: getAuthHeader() });
            const aiMsg = { message_id: `ai-${Date.now()}`, id: `ai-${Date.now()}`, role: 'assistant', content: res.data.response, timestamp: new Date().toISOString() };
            setLegacyMessages(prev => [...prev, aiMsg]);
            if (!activeConvId) {
                setActiveConvId(res.data.conversation_id);
                loadConversations();
            }
            return res.data.response;
        } catch {
            setLegacyMessages(prev => prev.filter(m => m.message_id !== userMsg.message_id));
            return null;
        } finally { setLegacyLoading(false); }
    }, [loading, activeConvId, getAuthHeader, loadConversations, chat]);

    // Input handling — hooks must be called unconditionally
    const legacyHandleInputChange = useCallback((e) => setLegacyInput(e.target.value), []);
    const legacyHandleSubmit = useCallback((e) => {
        e?.preventDefault?.();
        if (legacyInput.trim()) {
            sendMessage(legacyInput);
            setLegacyInput('');
        }
    }, [legacyInput, sendMessage]);

    const handleInputChange = USE_BFF ? chat.handleInputChange : legacyHandleInputChange;
    const handleSubmit = USE_BFF ? chat.handleSubmit : legacyHandleSubmit;
    const input = USE_BFF ? chat.input : legacyInput;

    return {
        conversations,
        messages,
        activeConvId,
        loading,
        status: USE_BFF ? chat.status : (legacyLoading ? 'submitted' : 'ready'),
        loadingConvs,
        messagesEndRef,
        loadConversations,
        loadConversation,
        startNewConversation,
        sendMessage,
        input,
        handleInputChange,
        handleSubmit,
        stop: USE_BFF ? chat.stop : () => {},
    };
}
